import { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { Table } from "reactstrap";
import { Button } from "../../Styled";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import {
  getLineReviewKey,
  OBSOLETE_ALT_SELECTED_ROW_CLASS,
} from "../../../constants/digiKeyColumns";
import { DIGIKEY_VIEW_OPTIONS } from "../../../constants/urls";

const formatPrice = (option) => {
  if (option.unit_price_display) {
    return option.unit_price_display;
  }
  if (option.unit_price != null) {
    return `$${Number(option.unit_price).toFixed(5)}`;
  }
  return "N/A";
};

const formatAvailability = (option) => {
  const value = option.quantity_available;
  if (value == null || String(value).trim() === "") {
    return "0";
  }
  return String(value).replace(/,/g, "");
};

const ViewOptionsTable = ({ options, selectedMpn, onSelect, radioName }) => {
  if (!options.length) {
    return (
      <p className="text-muted mb-0">
        No suggested parts were returned by DigiKey for this MPN.
      </p>
    );
  }

  return (
    <Table className="table-bordered table-sm mb-0 unmatched-view-options-table">
      <thead>
        <tr>
          <th />
          <th>Part Number</th>
          <th>Availability</th>
          <th>Unit Price</th>
        </tr>
      </thead>
      <tbody>
        {options.map((option, index) => {
          const mpn = String(option.manufacturer_part_number || "").trim();
          const isSelected = selectedMpn === mpn;

          return (
            <tr
              key={`${mpn}-${index}`}
              className={classnames({
                [OBSOLETE_ALT_SELECTED_ROW_CLASS]: isSelected,
              })}
              onClick={() => onSelect(mpn)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <input
                  type="radio"
                  name={radioName}
                  checked={isSelected}
                  onChange={() => onSelect(mpn)}
                />
              </td>
              <td>
                <div className="unmatched-option-mpn">{mpn}</div>
                {option.description ? (
                  <div className="text-muted small">{option.description}</div>
                ) : null}
                {option.manufacturer ? (
                  <div className="text-muted small">{option.manufacturer}</div>
                ) : null}
                {option.package ? (
                  <div className="text-muted small">{option.package}</div>
                ) : null}
              </td>
              <td>{formatAvailability(option)}</td>
              <td>
                <div>{formatPrice(option)}</div>
                {option.package ? (
                  <div className="text-muted small">{option.package}</div>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const UnmatchedPartsReviewModal = ({
  show,
  reviewLine,
  token,
  onSave,
  onClose,
}) => {
  const line = reviewLine || null;
  const lineKey = line ? getLineReviewKey(line) : "";
  const originalMpn = String(
    line?.unmatched_original_mpn || line?.manufacturer_part_number || ""
  ).trim();

  const [options, setOptions] = useState([]);
  const [totalFound, setTotalFound] = useState(0);
  const [selectedMpn, setSelectedMpn] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !line) {
      return;
    }

    setSelectedMpn(
      line.selected_alternate_mpn
        ? String(line.selected_alternate_mpn).trim()
        : ""
    );
    setOptions([]);
    setTotalFound(0);
    setLoadError("");
  }, [show, line, lineKey]);

  useEffect(() => {
    if (!show || !line || !originalMpn || !token) {
      return;
    }

    const cachedOptions = Array.isArray(line.substitutes)
      ? line.substitutes.filter(
          (item) => String(item?.manufacturer_part_number || "").trim() !== ""
        )
      : [];

    // Use cache when we still have the full DigiKey view-options list.
    // If only one row remains after an older save bug, refresh from DigiKey.
    const canUseCachedOptions =
      cachedOptions.length > 1 ||
      (cachedOptions.length === 1 && !line.selected_alternate_mpn);

    if (canUseCachedOptions) {
      setOptions(cachedOptions);
      setTotalFound(cachedOptions.length);
      setLoadError("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadOptions = async () => {
      setLoading(true);
      setLoadError("");

      const url =
        localStorage.getItem("isDebug") === "1"
          ? `${DIGIKEY_VIEW_OPTIONS}?debug=1&part_number=${encodeURIComponent(originalMpn)}`
          : `${DIGIKEY_VIEW_OPTIONS}?part_number=${encodeURIComponent(originalMpn)}`;

      try {
        const res = await fetch(url, {
          method: "GET",
          headers: {
            authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();

        if (!data || data.code == null || !(data.code >= 200 && data.code < 400)) {
          if (!cancelled) {
            setLoadError(
              (data && data.message) || "Failed to load DigiKey view options."
            );
            setOptions(cachedOptions);
            setTotalFound(cachedOptions.length);
          }
          return;
        }

        const loaded = data.data?.options || data.data?.substitutes || [];
        if (!cancelled) {
          setOptions(loaded);
          setTotalFound(
            data.data?.total_found != null
              ? Number(data.data.total_found)
              : loaded.length
          );
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError("Failed to load DigiKey view options.");
          setOptions(cachedOptions);
          setTotalFound(cachedOptions.length);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, [show, line, originalMpn, token, lineKey]);

  const selectedOption = useMemo(
    () =>
      options.find(
        (item) =>
          String(item.manufacturer_part_number || "").trim() === selectedMpn
      ) || null,
    [options, selectedMpn]
  );

  if (!show || !line) {
    return null;
  }

  const handleSave = async () => {
    if (!selectedOption) {
      return;
    }

    setSaving(true);
    try {
      await onSave({ line, alternate: selectedOption, options });
    } finally {
      setSaving(false);
    }
  };

  const isBusy = loading || saving;

  return (
    <ModalOverlay className={classnames({ show })}>
      <ModalBox
        className="theme-form unmatched-view-options-modal"
        style={{ maxWidth: "960px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
      >
        {isBusy ? (
          <div className="unmatched-modal-loading-overlay" aria-live="polite">
            <div className="unmatched-modal-spinner" aria-hidden="true" />
            <p className="mb-0">
              {saving
                ? "Saving selection…"
                : "Loading view options…"}
            </p>
          </div>
        ) : null}

        <div className="unmatched-parts-modal-header">
          <h5 className="mb-0">Exact Match Not Found</h5>
          <button
            type="button"
            className="unmatched-parts-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="unmatched-view-options-meta mb-3">
          <div>
            <strong>{originalMpn}</strong>
          </div>
          <div className="text-muted small">
            Results: {loading ? "…" : totalFound}
          </div>
        </div>

        <p className="text-muted mb-3">
          Please modify your entry or select a suggested part below.
        </p>

        {loadError ? <p className="text-danger mb-2">{loadError}</p> : null}
        {!loading ? (
          <ViewOptionsTable
            options={options}
            selectedMpn={selectedMpn}
            radioName={`unmatched-view-option-${lineKey}`}
            onSelect={setSelectedMpn}
          />
        ) : null}

        <div className="unmatched-parts-modal-footer d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">
            {selectedMpn ? `Selected: ${selectedMpn}` : "Select a suggested part"}
          </span>
          <div className="unmatched-parts-modal-actions d-flex align-items-center">
            <Button
              type="button"
              className="importBtn blue px-4 w-auto mr-2"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="importBtn blue px-4 w-auto"
              disabled={saving || loading || !selectedOption}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Selection"}
            </Button>
          </div>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default UnmatchedPartsReviewModal;
